
import { defineComponent, reactive, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonEntryRepository = RepositoryFactory.get('seasonEntry')
const SeasonGroupRepository = RepositoryFactory.get('seasonGroup')
const SeasonGroupingRepository = RepositoryFactory.get('seasonGrouping')

import draggable from 'vuedraggable'

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'


import { Season } from '@/types/season'
import { Group } from '@/types/group'
import { Custom } from '@/types/custom'
import { EntryTeam } from '@/types/entryTeam'

interface EntryTeamForGrouping extends EntryTeam {
  isBelonged?: boolean
}

export default defineComponent({
  name: 'SeasonGroupDetail',
  inheritAttrs: false,
  components: {
    draggable,
    BaseBreadcrumb,
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getGroups()
      getGroup()
    })

    watch(
      () => [route.params.group_id],
      () => {
        if (route.name === 'seasonGroupDetail') {
          getGroups()
          getGroup()

          grouping.in_team_id = undefined
          grouping.out_team_id = undefined
        }
      }
    )

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const entries: {
      list: EntryTeamForGrouping[]
    } = reactive({
      list: []
    })

    const searchForm = reactive({
      keyword: ''
    })

    watch(
      () => searchForm,
      () => {
        getEntries()
      },
      { deep: true }
    )

    const isSearching = computed(() => {
      if (searchForm.keyword !== '') {
        return true
      } else {
        return false
      }
    })

    const getEntries = async () => {
      try {
        const { season_id } = route.params

        const dataObj = {
          keyword: searchForm.keyword
        }

        const res = await SeasonEntryRepository.getEntries(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          entries.list = data.entries

          sortEntries()
        }
      } catch (error) {
        //
      }
    }

    const sortEntries = () => {
      let belongTeams: EntryTeamForGrouping[] = []
      let unBelongTeams: EntryTeamForGrouping[] = []

      entries.list.forEach((entry) => {
        const hasEntryTeam = group.teams.some(team => team.entry_team_id === entry.id)
        if (hasEntryTeam) {
          entry.isBelonged = true
          belongTeams.push(entry)
        } else {
          entry.isBelonged = false
          unBelongTeams.push(entry)
        }
      })

      entries.list = unBelongTeams.concat(belongTeams)
    }

    const groups: {
      customList: Custom[]
      custom?: Custom

      list: Group[]
    } = reactive({
      customList: [],
      custom: undefined,

      list: []
    })

    const getGroups = async () => {
      try {
        const { season_id, group_id } = route.params

        const res = await SeasonGroupRepository.getGroups(season_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season
          groups.customList = data.groups

          for (let i=0; i < groups.customList.length; i++) {
            const hasCurrentGroup = groups.customList[i].groups.some((group: Group) => String(group.id) === group_id)

            if (hasCurrentGroup) {
              groups.custom = groups.customList[i]
              groups.list = groups.customList[i].groups
            }
          }
        }
      } catch (error) {
        //
      }
    }

    const group: {
      data?: Group
      teams: EntryTeam[]
    } = reactive({
      data: undefined,
      teams: []
    })

    const getGroup = async () => {
      try {
        const { season_id, group_id } = route.params

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGroupingRepository.getGrouping(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          group.data = data.group
          group.teams = data.teams

          getEntries()
        }
      } catch (error) {
        //
      }
    }

    const grouping: {
      in_team_id?: number
      out_team_id?: number
    } = reactive({
      in_team_id: undefined,
      out_team_id: undefined
    })

    const isLoadingGrouping = reactive({
      inTeam: false,
      exchangeTeams: false,
      outTeam: false
    })

    const selectInTeam = (id: number) => {
      grouping.in_team_id = (grouping.in_team_id === id) ? undefined : id
    }

    const selectOutTeam = (id: number) => {
      grouping.out_team_id = (grouping.out_team_id === id) ? undefined : id
    }

    const isOnlyInTeamSelected = computed(() => {
      return grouping.in_team_id == undefined || grouping.out_team_id != undefined
    })

    const isOnlyOutTeamSelected = computed(() => {
      return grouping.in_team_id != undefined || grouping.out_team_id == undefined
    })

    const isInAndOutTeamSelected = computed(() => {
      return grouping.in_team_id == undefined || grouping.out_team_id == undefined
    })

    const divideTeam = async (type: 'in' | 'exchange' | 'out') => {
      try {
        const { season_id, group_id } = route.params

        let in_team_id
        let out_team_id

        if (type === 'in') {
          isLoadingGrouping.inTeam = true

          in_team_id = grouping.in_team_id
          out_team_id = null
        } else if (type === 'exchange') {
          isLoadingGrouping.exchangeTeams = true

          in_team_id = grouping.in_team_id
          out_team_id = grouping.out_team_id
        } else if (type === 'out') {
          isLoadingGrouping.outTeam = true

          in_team_id = null
          out_team_id = grouping.out_team_id
        }

        const dataObj = {
          group_id: group_id,
          in_team_id: in_team_id,
          out_team_id: out_team_id
        }

        const res = await SeasonGroupingRepository.postGrouping(season_id, dataObj)

        if (res.status === 200) {
          getGroup()
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'チーム分けを更新しました'
          })

          if (type === 'in') {
            grouping.in_team_id = undefined
          } else if (type === 'exchange') {
            grouping.in_team_id = undefined
            grouping.out_team_id = undefined
          } else if (type === 'out') {
            grouping.out_team_id = undefined
          }
        }
      } catch (error) {
        //
      } finally {
        isLoadingGrouping.inTeam = false
        isLoadingGrouping.exchangeTeams = false
        isLoadingGrouping.outTeam = false
      }
    }

    const updateAllocateCode = async (id: number, allocate_code: string) => {
      try {
        const { season_id, group_id } = route.params

        const dataObj = {
          group_id: group_id,
          entry_team_id: id,
          allocate_code: allocate_code
        }

        const res = await SeasonGroupingRepository.updateGrouping(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          getGroup()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'ブロック内コードを変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const updateTeamPriority = async () => {
      try {
        let priorities: {
          id: number
          priority: number
        }[] = []

        group.teams.forEach((team, index) => {
          priorities.push({
            id: team.id as number,
            priority: index + 1
          })
        })

        const { season_id, group_id } = route.params

        const dataObj = {
          priorities: priorities
        }

        const res = await SeasonGroupingRepository.updateGroupingPriority(season_id, group_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'チームの並び順を変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    useHead({
      title: computed(() => `グループ分け - ${season.data?.topic_name} ${season.data?.name}`)
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id,
          },
          setting: {
            path: '/event/' + route.params.topic_id
          }
        },
        {
          type: 'select',
          select: {
            type: 'grouping',
            currentId: groups.custom?.id,
            list: groups.customList
          }
        }
      ] as Breadcrumb[]
    })

    return {
      groups,
      getGroups,

      entries,
      searchForm,
      isSearching,
      getEntries,
      sortEntries,

      group,
      getGroup,

      grouping,
      isLoadingGrouping,
      selectInTeam,
      selectOutTeam,
      isOnlyInTeamSelected,
      isOnlyOutTeamSelected,
      isInAndOutTeamSelected,
      divideTeam,

      updateAllocateCode,
      updateTeamPriority,

      breadcrumbs
    }
  }
})
